/**
 * Verificación biométrica guiada — camera-bridge.
 *
 * En vez de mandar al usuario al flujo del proveedor, capturamos SU cámara y la
 * transmitimos en vivo por WebRTC al scrapper (que la inyecta como cámara
 * virtual en el flujo de liveness del proveedor). El usuario sólo ve su propia
 * cámara y una guía clara en español; el estado del proceso llega por SSE.
 */

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { usePaymentStream } from './usePaymentStream.js';
import { useBiometricCamera } from './useBiometricCamera.js';
import { isFailed, type PaymentSnapshot } from './scrapperApi.js';

export interface BiometricVerificationProps {
  /** Base URL del payments-api (scrapper), ej. "https://pay.kaonstudios.com". */
  scrapperApiUrl: string;
  /** payment_id (UUID) devuelto al crear el payment en el scrapper. */
  paymentId: string;
  /** Se dispara una vez cuando el KYC queda verificado. */
  onVerified?: () => void;
  /** Se dispara una vez cuando el payment termina en un estado de fallo. */
  onFailed?: (info: { status: string; code: string | null; message: string | null }) => void;
}

type Phase =
  | 'connecting'
  | 'preparing'
  | 'capture'
  | 'verified'
  | 'rejected'
  | 'timeout'
  | 'failed'
  | 'notFound';

const VERIFIED_STATUSES: ReadonlySet<string> = new Set([
  'KYC_VERIFIED',
  'CONFIRMED',
  'ORDER_CAPTURED',
  'DONE',
]);

const STATUS_LABEL: Record<string, string> = {
  CREATED: 'Iniciando…',
  AUTOMATION_STARTED: 'Conectando con el proveedor…',
  AWAITING_OTP: 'Verificando tu correo…',
  OTP_RECEIVED: 'Correo verificado…',
  AT_WALLET: 'Configurando tu wallet…',
  WALLET_FILLED: 'Wallet lista…',
  AT_CARD: 'Procesando tu tarjeta…',
  AT_CONFIRM: 'Confirmando…',
  AT_KYC_PERSONAL: 'Revisando tus datos…',
  AT_KYC_ADDRESS: 'Revisando tu dirección…',
  AT_KYC_PURPOSE: 'Casi listo para la verificación…',
  AWAITING_BIOMETRIC: 'Verificación de identidad',
  KYC_VERIFIED: 'Identidad verificada',
  CONFIRMED: 'Confirmando tu compra…',
  ORDER_CAPTURED: 'Procesando tu compra…',
  DONE: 'Compra completada',
};

// Paso del journey de 5 (los primeros 3 los lleva PaymentForm). Solo aplica a
// las fases del biométrico.
const STEP_OF_PHASE: Partial<Record<Phase, { n: number; label: string }>> = {
  preparing: { n: 4, label: 'Verificación facial' },
  capture: { n: 4, label: 'Verificación facial' },
  verified: { n: 5, label: 'Listo' },
};

// Texto que rota mientras el scrapper trabaja (paso 3→4): da contexto de que el
// proceso tarda 3-4 min sin que el cliente crea que se colgó. Rota cada ~7s.
const PREPARING_MESSAGES: readonly string[] = [
  'Procesando tu información…',
  'Esto puede tomar 3 o 4 minutos. No cierres esta ventana.',
  'Poniendo todo en orden…',
  'Verificando tus datos con el proveedor de identidad…',
  'Configurando tu verificación facial…',
  'Casi listo, preparando la cámara…',
];

const phaseOf = (snap: PaymentSnapshot | null, notFound: boolean): Phase => {
  if (notFound) return 'notFound';
  if (snap === null) return 'connecting';
  if (snap.kycVerifiedAt !== null || VERIFIED_STATUSES.has(snap.status)) return 'verified';
  if (snap.status === 'FAILED_KYC_REJECTED') return 'rejected';
  if (snap.status === 'FAILED_KYC_TIMEOUT') return 'timeout';
  if (isFailed(snap.status)) return 'failed';
  if (snap.status === 'AWAITING_BIOMETRIC') return 'capture';
  return 'preparing';
};

export const BiometricVerification = (props: BiometricVerificationProps) => {
  const { scrapperApiUrl, paymentId, onVerified, onFailed } = props;
  const { snapshot, transport, notFound, biometricPrompt, documentRequest } = usePaymentStream(
    scrapperApiUrl,
    paymentId,
  );
  const phase = phaseOf(snapshot, notFound);

  const camera = useBiometricCamera(scrapperApiUrl, paymentId, phase === 'capture');

  // Texto rotando durante la espera (paso 3→4). Avanza cada 7s mientras la fase
  // sea 'preparing'; se reinicia al salir de esa fase.
  const [prepIdx, setPrepIdx] = useState(0);
  useEffect(() => {
    if (phase !== 'preparing') {
      setPrepIdx(0);
      return;
    }
    const id = setInterval(
      () => setPrepIdx((i) => (i + 1) % PREPARING_MESSAGES.length),
      7000,
    );
    return () => clearInterval(id);
  }, [phase]);

  const stepInfo = STEP_OF_PHASE[phase];

  // Adjuntar el stream local al <video> de preview.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (el && el.srcObject !== camera.stream) {
      el.srcObject = camera.stream;
    }
  }, [camera.stream, phase]);

  // Callbacks terminales — una sola vez.
  const firedVerified = useRef(false);
  const firedFailed = useRef(false);
  useEffect(() => {
    if (phase === 'verified' && !firedVerified.current) {
      firedVerified.current = true;
      onVerified?.();
    }
    if (
      (phase === 'rejected' || phase === 'timeout' || phase === 'failed') &&
      !firedFailed.current
    ) {
      firedFailed.current = true;
      onFailed?.({
        status: snapshot?.status ?? 'UNKNOWN',
        code: snapshot?.errorCode ?? null,
        message: snapshot?.errorMessage ?? null,
      });
    }
  }, [phase, snapshot, onVerified, onFailed]);

  return (
    <div className="wp-card">
      <div className="wp-header">
        <h2 className="wp-title">Verificación de identidad</h2>
        {stepInfo ? (
          <p className="wp-subtitle">
            Paso {stepInfo.n} de 5 · {stepInfo.label}
          </p>
        ) : (
          <p className="wp-subtitle">Un paso rápido y seguro para proteger tu compra.</p>
        )}
      </div>

      {phase === 'connecting' && (
        <div className="wp-bio-center">
          <span className="wp-spinner wp-spinner-dark" />
          <p className="wp-bio-muted">Conectando…</p>
        </div>
      )}

      {phase === 'notFound' && (
        <div className="wp-banner wp-banner-error">
          No encontramos tu verificación. Refresca la página o vuelve a iniciar tu compra.
        </div>
      )}

      {phase === 'preparing' && (
        <div>
          <div className="wp-bio-center">
            <span className="wp-spinner wp-spinner-dark" />
            <p className="wp-bio-prepare-msg" aria-live="polite">
              {PREPARING_MESSAGES[prepIdx]}
            </p>
            <p className="wp-bio-muted">No cierres esta ventana.</p>
          </div>
          <StatusStrip snapshot={snapshot} transport={transport} pulse />
        </div>
      )}

      {phase === 'capture' && documentRequest && (
        <DocumentUpload
          scrapperApiUrl={scrapperApiUrl}
          paymentId={paymentId}
          side={documentRequest}
        />
      )}

      {phase === 'capture' && !documentRequest && (
        <div>
          <p className="wp-bio-instruction">
            Coloca tu rostro dentro del marco y, cuando te lo pida, gira la cabeza
            despacio hacia <b>la izquierda y la derecha</b> (a ambos lados).
          </p>

          <div className="wp-bio-cam-wrap">
            {/* El <video> se mantiene montado (el ref necesita el nodo para
                adjuntar el stream) pero invisible hasta que la cámara conecta —
                no le mostramos al usuario su cámara hasta tenerla lista. */}
            <video
              ref={videoRef}
              className="wp-bio-video"
              autoPlay
              playsInline
              muted
              style={{ opacity: camera.state === 'streaming' ? 1 : 0 }}
            />
            {camera.state === 'streaming' ? (
              <>
                {/* Guía de posición: marco de esquinas (replica el de Onfido). */}
                <div className="wp-bio-frame" aria-hidden="true">
                  <span className="wp-bio-corner tl" />
                  <span className="wp-bio-corner tr" />
                  <span className="wp-bio-corner bl" />
                  <span className="wp-bio-corner br" />
                </div>
                {biometricPrompt && (
                  <div className="wp-bio-prompt" role="status" aria-live="polite">
                    {biometricPrompt}
                  </div>
                )}
              </>
            ) : (
              camera.state !== 'denied' &&
              camera.state !== 'error' && (
                <div className="wp-bio-cam-connecting">
                  <span className="wp-spinner" />
                  <span>Conectando tu cámara…</span>
                </div>
              )
            )}
            <CameraBadge state={camera.state} />
          </div>
          {camera.state === 'streaming' && biometricPrompt && (
            <p className="wp-bio-prompt-hint">👆 Sigue esta indicación frente a la cámara</p>
          )}

          {camera.state === 'denied' && (
            <div className="wp-banner wp-banner-error">
              {camera.errorMessage}
              <button type="button" className="wp-button" style={{ marginTop: 10 }} onClick={camera.retry}>
                Permitir cámara y reintentar
              </button>
            </div>
          )}
          {camera.state === 'error' && (
            <div className="wp-banner wp-banner-error">
              {camera.errorMessage}
              <button type="button" className="wp-button" style={{ marginTop: 10 }} onClick={camera.retry}>
                Reintentar
              </button>
            </div>
          )}

          <StatusStrip snapshot={snapshot} transport={transport} pulse />
        </div>
      )}

      {phase === 'verified' && (
        <div className="wp-bio-center">
          <div className="wp-bio-badge wp-bio-badge-ok">✓</div>
          <h3 className="wp-bio-result-title">¡Identidad verificada!</h3>
          <p className="wp-bio-muted">Todo listo. Continuamos con tu compra automáticamente.</p>
        </div>
      )}

      {phase === 'rejected' && (
        <div className="wp-bio-center">
          <div className="wp-bio-badge wp-bio-badge-err">✗</div>
          <h3 className="wp-bio-result-title">No pudimos verificarte</h3>
          <p className="wp-bio-muted">
            {snapshot?.errorMessage ??
              'La verificación no pasó. Revisa que las fotos sean nítidas y vuelve a intentarlo.'}
          </p>
        </div>
      )}

      {phase === 'timeout' && (
        <div className="wp-bio-center">
          <div className="wp-bio-badge wp-bio-badge-warn">⏱</div>
          <h3 className="wp-bio-result-title">Se agotó el tiempo</h3>
          <p className="wp-bio-muted">
            La verificación tardó demasiado. Vuelve a iniciar tu compra para intentarlo de nuevo.
          </p>
        </div>
      )}

      {phase === 'failed' && (
        <div className="wp-bio-center">
          <div className="wp-bio-badge wp-bio-badge-err">!</div>
          <h3 className="wp-bio-result-title">Algo salió mal</h3>
          <p className="wp-bio-muted">
            {snapshot?.errorMessage ?? 'No pudimos completar el proceso. Intenta de nuevo más tarde.'}
          </p>
        </div>
      )}
    </div>
  );
};

interface DocumentUploadProps {
  scrapperApiUrl: string;
  paymentId: string;
  side: string; // 'front' | 'back'
}

/** Subida de la cédula: el cliente toma/elige una foto y se envía al scrapper,
 * que la inyecta en el file-input de Onfido. */
const DocumentUpload = ({ scrapperApiUrl, paymentId, side }: DocumentUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset al cambiar el lado pedido (delantera → trasera).
  useEffect(() => {
    setFile(null);
    setPreview(null);
    setSent(false);
    setError(null);
  }, [side]);

  const label = side === 'back' ? 'trasera (reverso)' : 'delantera (frente)';

  const onPick = (e: ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
    setError(null);
  };

  const onSend = async (): Promise<void> => {
    if (!file) return;
    setSending(true);
    setError(null);
    try {
      // Subimos la imagen ORIGINAL sin redimensionar: Onfido necesita leer el
      // número y el texto de la cédula nítidos; el downscaling la dejaba borrosa.
      // El límite de tamaño se maneja en nginx (client_max_body_size 25m).
      const fd = new FormData();
      fd.append('side', side);
      fd.append('file', file);
      const r = await fetch(`${scrapperApiUrl}/payments/${paymentId}/document`, {
        method: 'POST',
        body: fd,
      });
      if (r.status === 413) {
        setError('La foto es muy pesada (máx. 10 MB). Toma una con menos resolución.');
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSent(true);
    } catch {
      setError('No pudimos enviar la imagen. Reintenta.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="wp-bio-center">
        <span className="wp-spinner wp-spinner-dark" />
        <p className="wp-bio-prepare-msg">Imagen enviada. Procesando…</p>
        <p className="wp-bio-muted">No cierres esta ventana.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="wp-bio-instruction">
        Toma o sube una foto de la parte <b>{label}</b> de tu cédula. Que se vea
        nítida, completa y sin reflejos.
      </p>
      <label className="wp-bio-doc-drop">
        {preview ? (
          <img src={preview} className="wp-bio-doc-preview" alt="vista previa" />
        ) : (
          <span className="wp-bio-doc-placeholder">📷 Tomar o elegir foto</span>
        )}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPick}
          style={{ display: 'none' }}
        />
      </label>
      {error && <div className="wp-banner wp-banner-error">{error}</div>}
      <button
        type="button"
        className="wp-button"
        style={{ marginTop: 12 }}
        disabled={!file || sending}
        onClick={() => void onSend()}
      >
        {sending ? 'Enviando…' : 'Enviar foto'}
      </button>
    </div>
  );
};

const CAMERA_LABEL: Record<string, string> = {
  idle: '',
  requesting: 'Pidiendo permiso de tu cámara…',
  connecting: 'Conectando…',
  streaming: 'Cámara conectada',
  denied: 'Cámara bloqueada',
  error: 'Error de cámara',
};

const CameraBadge = ({ state }: { state: string }) => {
  if (state === 'idle' || state === 'denied' || state === 'error') return null;
  const live = state === 'streaming';
  return (
    <div className={`wp-bio-cam-badge${live ? ' wp-bio-cam-badge-live' : ''}`}>
      {!live && <span className="wp-spinner wp-spinner-dark wp-spinner-sm" />}
      <span>{CAMERA_LABEL[state]}</span>
    </div>
  );
};

interface StatusStripProps {
  snapshot: PaymentSnapshot | null;
  transport: 'connecting' | 'sse' | 'polling';
  pulse?: boolean;
}

const StatusStrip = ({ snapshot, transport, pulse }: StatusStripProps) => {
  const label = snapshot ? (STATUS_LABEL[snapshot.status] ?? snapshot.status) : 'Conectando…';
  const live = transport !== 'connecting';
  return (
    <div className="wp-bio-strip">
      <span
        className={`wp-bio-dot${pulse ? ' wp-bio-dot-pulse' : ''}${live ? ' wp-bio-dot-live' : ''}`}
      />
      <span className="wp-bio-strip-text">{label}</span>
    </div>
  );
};
